import React, { createContext, useContext, useState } from 'react';
import axiosInstance from '../api/axios';

const ControlSpecContext = createContext();

export const useControlSpec = () => {
    const context = useContext(ControlSpecContext);
    if (!context) {
        throw new Error('useControlSpec must be used within ControlSpecProvider');
    }
    return context;
};

export const ControlSpecProvider = ({ children }) => {
    const [controlSpec, setControlSpec] = useState(null);
    const [conversation, setConversation] = useState([]);
    const [isRefining, setIsRefining] = useState(false);
    const [changeLog, setChangeLog] = useState([]);
    const [error, setError] = useState(null);

    const refineWithChat = async (userMessage) => {
        if (!controlSpec || !userMessage.trim()) {
            return;
        }

        setIsRefining(true);
        setError(null);

        try {
            const response = await axiosInstance.post('/api/controls/refine-chat', {
                current_control_spec: controlSpec,
                user_message: userMessage,
                conversation_history: conversation
            });
            const data = response.data;

            // Update state atomically
            setControlSpec(data.updated_control_spec);
            setConversation(prev => [
                ...prev,
                { role: 'user', content: userMessage },
                { role: 'assistant', content: data.ai_message }
            ]);
            setChangeLog(prev => [...prev, ...data.changes_summary]);

            // Clear change highlights after 3 seconds
            setTimeout(() => {
                setChangeLog([]);
            }, 3000);

        } catch (err) {
            console.error('Refinement error:', err);
            setError(err.message);
            setConversation(prev => [
                ...prev,
                { role: 'user', content: userMessage },
                { role: 'error', content: `Error: ${err.message}` }
            ]);
        } finally {
            setIsRefining(false);
        }
    };

    const initializeControlSpec = (spec) => {
        setControlSpec(spec);
        setConversation([]);
        setChangeLog([]);
        setError(null);
    };

    const resetConversation = () => {
        setConversation([]);
        setChangeLog([]);
        setError(null);
    };

    return (
        <ControlSpecContext.Provider
            value={{
                controlSpec,
                conversation,
                isRefining,
                changeLog,
                error,
                refineWithChat,
                initializeControlSpec,
                resetConversation
            }}
        >
            {children}
        </ControlSpecContext.Provider>
    );
};
